// Editar los menús de una jornada del multievento (pestaña Líneas y menús del
// formato nuevo): añadir líneas contratadas en la cabecera ("Añadir a esta
// jornada", con su precio, IVA, concepto, bebida y personal de la cabecera y
// las personas de la jornada), cambiar las unidades, las horas de servicio y
// la bebida incluida de cada línea, y quitar. Una hora cambiada aquí es la
// "hora propia" del día (`hora_servicio_manual: true`): la hora que se pone
// desde el Resumen para todos los días no la pisa; "Volver a la del resto"
// deja la de la cabecera y la vuelve a sincronizar. Se guarda en una sola llamada:
// PUT de la jornada con su `menus_personalizados` completo. El precio y el
// IVA no se tocan aquí: siguen a la cabecera.
//
// Sin el módulo de presupuestos (F1, 16-oct-2026) las horas se ven como texto.
// Avisos en ámbar: "Sin hora de fin" (F3) y "Fuera del horario del día" (F6).
//
// Props: presupuesto, jornada, numero, onGuardar(menus) -> Promise<boolean>, onCerrar
import React, { useMemo, useState } from 'react';
import usePuedeEditarHorarios from '../../hooks/usePuedeEditarHorarios';
import ModalJornada, { claseBotonPrincipal, claseBotonSecundario } from './ModalJornada';
import {
  TEXTO_PRECIO_EN_CABECERA, claveLinea, esLineaBebida, esLineaVentaAparte, estadoHoraServicioJornada, lineaCabecera,
  lineaResuelta, precioEfectivo, quitarLineaConAcompana,
} from '../../utils/multieventoCabecera';
import {
  CLASE_ERROR_FRANJA, errorFranja, fueraDeHorario, horarioReferenciaJornada, textoHorasServicio,
} from '../../utils/horasServicio';
import {
  anadirLineaCabeceraAJornada, distribucionMenusJornadas, jornadaTieneLinea, textoJornada,
} from '../../utils/multieventoPantallas';

const formatEuros = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const nombreDe = (l) => l?.nombre_menu || l?.nombre || 'Línea';
const esBarraVinculada = (l) => !!(l && l.es_barra_libre_lunch && l.barra_libre_de_linea_id);

const EditarMenusJornadaModal = ({ presupuesto, jornada, numero, onGuardar, onCerrar }) => {
  const raiz = useMemo(() => (presupuesto?.menus_personalizados || []).filter(Boolean), [presupuesto]);
  const contratadas = useMemo(() => raiz.filter(m => !esLineaVentaAparte(m)), [raiz]);
  const distribucion = useMemo(
    () => distribucionMenusJornadas(raiz, presupuesto?.jornadas || []),
    [raiz, presupuesto]
  );
  const [lineas, setLineas] = useState(() => (jornada?.menus_personalizados || []).filter(Boolean).map(l => ({ ...l })));
  const [guardando, setGuardando] = useState(false);
  const personasJornada = Number(jornada?.num_personas) || 1;
  const puedeEditarHoras = usePuedeEditarHorarios();
  const [refIni, refFin] = horarioReferenciaJornada(jornada, presupuesto);

  const cambiar = (pos, cambios) => setLineas(prev => prev.map((l, i) => (i === pos ? { ...l, ...cambios } : l)));
  // Quitar: los complementos de la jornada siguen con su servicio ("Acompaña a" por índice)
  const quitar = (pos) => setLineas(prev => quitarLineaConAcompana(prev, pos));
  const anadir = (lineaCab) => setLineas(prev => anadirLineaCabeceraAJornada(prev, lineaCab, personasJornada, raiz));

  const resueltas = lineas.map(l => lineaResuelta(l, raiz));
  const totalPersonas = resueltas
    .filter(l => !esLineaVentaAparte(l))
    .reduce((s, l) => s + (Number(l.num_personas) || 0), 0);
  const importe = resueltas.reduce((s, l) => {
    const precio = precioEfectivo(l) || 0;
    const uds = l.precio_unitario !== undefined && l.precio_unitario !== null
      ? Number(l.num_personas) || 0
      : Number(l.uds ?? l.num_personas) || 0;
    return s + precio * uds;
  }, 0);

  // Unidades sin poner o pareja de horas inválida (fin antes que inicio): no se guarda
  const hayMalas = lineas.some(l => !esLineaVentaAparte(l) && (!((parseInt(l.num_personas, 10) || 0) > 0)
    || !!errorFranja(l.hora_inicio_servicio, l.hora_fin_servicio)));
  const guardar = async () => {
    if (hayMalas) return;
    setGuardando(true);
    const ok = await onGuardar(lineas.map(l => (esLineaVentaAparte(l) ? l : { ...l, num_personas: parseInt(l.num_personas, 10) })));
    setGuardando(false);
    if (ok) onCerrar();
  };

  return (
    <ModalJornada
      titulo="Editar menús de la jornada"
      subtitulo={`${textoJornada(jornada, numero)} · ${personasJornada} personas`}
      onCerrar={onCerrar}
      ancho="max-w-2xl"
      testid={`modal-menus-jornada-${jornada?.id}`}
      pie={(
        <>
          <button type="button" onClick={onCerrar} className={claseBotonSecundario}>Cancelar</button>
          <button type="button" onClick={guardar} disabled={guardando || hayMalas} className={claseBotonPrincipal}
            data-testid="menus-jornada-guardar">
            {guardando ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </>
      )}
    >
      <section className="flex flex-col gap-2">
        <h4 className="text-xs font-bold tracking-wider uppercase text-[#5B6B64]">Líneas contratadas en la cabecera</h4>
        {contratadas.length === 0 ? (
          <p className="text-sm text-[#5B6B64]">No hay líneas en la cabecera. Añádelas en la tabla de líneas de abajo.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-[#EEF1EF] rounded-lg border border-[#E1E6E3]">
            {contratadas.map((m, idx) => {
              const ya = jornadaTieneLinea(lineas, m, raiz);
              const asignado = distribucion[claveLinea(m)]?.texto;
              return (
                <li key={m.linea_id || `${m.menu_id || nombreDe(m)}-${idx}`} className="px-3 py-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#16221D]">{nombreDe(m)}</p>
                    <p className="text-xs text-[#5B6B64]">
                      {formatEuros(precioEfectivo(m))} por unidad{asignado ? ` · en las jornadas: ${asignado}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => anadir(m)}
                    disabled={ya}
                    className="h-11 md:h-8 w-full md:w-auto px-3 rounded-lg border border-duetto-primary text-duetto-primary text-sm md:text-xs font-semibold hover:bg-[#E3F3EC] disabled:border-[#CBD3CF] disabled:text-[#5B6B64] disabled:hover:bg-white"
                    data-testid={`menus-jornada-anadir-${idx}`}
                  >
                    {ya ? 'Ya está en la jornada' : 'Añadir a esta jornada'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h4 className="text-xs font-bold tracking-wider uppercase text-[#5B6B64]">Líneas de esta jornada</h4>
        <p className="text-xs text-[#5B6B64]" data-testid="menus-jornada-precio-cabecera">
          {TEXTO_PRECIO_EN_CABECERA}. Aquí solo se cambian las unidades, las horas de servicio y la bebida de la jornada.
        </p>
        {lineas.length === 0 ? (
          <p className="text-sm text-[#7A3A06] rounded-lg border border-[#F4D9AE] bg-[#FDF0DC] px-3 py-2">
            Sin líneas: esta jornada no cobra ni cocina nada. Añade las líneas de la cabecera que tocan este día.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {lineas.map((l, pos) => {
              const res = resueltas[pos];
              const aparte = esLineaVentaAparte(l);
              const vinculada = esBarraVinculada(l);
              const cab = aparte ? null : lineaCabecera(l, raiz);
              const bebidaIncluida = l.bebida_incluida !== undefined ? !!l.bebida_incluida : !!cab?.bebida_incluida;
              const bebidasPorPersona = l.bebidas_por_persona !== undefined ? l.bebidas_por_persona : (cab?.bebidas_por_persona || 1);
              const uds = parseInt(l.num_personas, 10) || 0;
              // ¿Esta línea sigue la hora del resto de días (la de la cabecera)?
              const estadoHora = cab ? estadoHoraServicioJornada(l, cab) : null;
              const errorHoras = errorFranja(l.hora_inicio_servicio, l.hora_fin_servicio);
              const sinFin = !!l.hora_inicio_servicio && !l.hora_fin_servicio && !errorHoras;
              const fuera = errorHoras ? null
                : fueraDeHorario(l.hora_inicio_servicio, l.hora_fin_servicio, refIni, refFin, 'día');
              return (
                <li key={`${l.linea_id || l.menu_id || nombreDe(l)}-${pos}`} className="rounded-lg border border-[#E1E6E3] px-3 py-2.5 flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#16221D]">{nombreDe(res)}</p>
                      <p className="text-xs text-[#5B6B64]">
                        {formatEuros(precioEfectivo(res))} por unidad (precio de la cabecera)
                        {!aparte && ` · subtotal ${formatEuros((precioEfectivo(res) || 0) * uds)}`}
                      </p>
                    </div>
                    {aparte ? (
                      <span className="text-xs text-[#5B6B64]">
                        {esLineaBebida(l) ? 'Se cambia en la pestaña Bebidas' : 'Se cambia en Material'}
                      </span>
                    ) : vinculada ? (
                      <span className="text-xs text-[#5B6B64]">{uds} uds · siguen a las personas del lunch</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => cambiar(pos, { num_personas: Math.max(1, uds - 1) })}
                          className="w-11 h-11 md:w-8 md:h-8 text-lg md:text-base rounded-full bg-gray-100 hover:bg-gray-200 text-[#16221D]" aria-label="Una menos">-</button>
                        <input
                          type="number"
                          min="1"
                          value={l.num_personas ?? ''}
                          onChange={(e) => cambiar(pos, { num_personas: e.target.value })}
                          className={`w-20 h-11 md:h-8 text-center text-base md:text-sm border rounded-lg ${uds > 0 ? 'border-[#CBD3CF]' : 'border-red-400'}`}
                          data-testid={`menus-jornada-uds-${pos}`}
                        />
                        <button type="button" onClick={() => cambiar(pos, { num_personas: uds + 1 })}
                          className="w-11 h-11 md:w-8 md:h-8 text-lg md:text-base rounded-full bg-[#E3F3EC] hover:bg-[#BFE3D2] text-[#0F5E43]" aria-label="Una más">+</button>
                        <button type="button" onClick={() => quitar(pos)}
                          className="ml-1 min-h-[44px] md:min-h-0 px-2 md:px-0 text-sm md:text-xs font-semibold text-red-700 hover:underline"
                          data-testid={`menus-jornada-quitar-${pos}`}>
                          Quitar
                        </button>
                      </div>
                    )}
                  </div>
                  {!aparte && !vinculada && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[#EEF1EF] pt-2">
                      {!puedeEditarHoras ? (
                        <span className="flex items-center gap-1.5 text-xs text-[#5B6B64]" data-testid={`menus-jornada-horas-texto-${pos}`}>
                          Servicio
                          <strong className="tabular-nums text-[#33443C]">
                            {textoHorasServicio(l.hora_inicio_servicio, l.hora_fin_servicio)}
                          </strong>
                        </span>
                      ) : (
                      <span className="flex flex-wrap md:flex-nowrap items-center gap-1.5 w-full md:w-auto text-[13px] md:text-xs text-[#5B6B64]">
                        Servicio
                        <input
                          type="time"
                          value={l.hora_inicio_servicio || ''}
                          placeholder={cab?.hora_inicio_servicio || ''}
                          onChange={(e) => cambiar(pos, { hora_inicio_servicio: e.target.value, hora_servicio_manual: true })}
                          className="h-11 md:h-8 flex-1 md:flex-none min-w-[6.5rem] md:min-w-0 px-2 text-base md:text-xs border border-[#CBD3CF] rounded-lg"
                          data-testid={`menus-jornada-hora-inicio-${pos}`}
                        />
                        a
                        <input
                          type="time"
                          value={l.hora_fin_servicio || ''}
                          onChange={(e) => cambiar(pos, { hora_fin_servicio: e.target.value, hora_servicio_manual: true })}
                          className={`h-11 md:h-8 flex-1 md:flex-none min-w-[6.5rem] md:min-w-0 px-2 text-base md:text-xs border rounded-lg ${sinFin ? 'border-[#F4D9AE] bg-[#FDF0DC]' : 'border-[#CBD3CF]'}`}
                          data-testid={`menus-jornada-hora-fin-${pos}`}
                        />
                        {errorHoras && (
                          <span className={CLASE_ERROR_FRANJA} data-testid={`menus-jornada-hora-error-${pos}`}>
                            {errorHoras}
                          </span>
                        )}
                      </span>
                      )}
                      {sinFin && (
                        <span className="px-1.5 py-px rounded text-xs md:text-[11px] font-semibold bg-[#FDF0DC] text-[#92400E]"
                          data-testid={`menus-jornada-sin-fin-${pos}`}>Sin hora de fin</span>
                      )}
                      {fuera && (
                        <span className="px-1.5 py-px rounded text-xs md:text-[11px] font-semibold bg-[#FDF0DC] text-[#92400E]"
                          data-testid={`menus-jornada-fuera-horario-${pos}`}>{fuera}</span>
                      )}
                      {estadoHora && (
                        <span className="flex flex-wrap items-center gap-2 text-xs md:text-[11px]" data-testid={`menus-jornada-hora-estado-${pos}`}>
                          <span className={estadoHora.propia || estadoHora.heredada ? 'font-semibold text-[#92400E]' : 'text-[#5B6B64]'}>
                            {estadoHora.propia ? 'Hora propia de este día'
                              : (estadoHora.heredada ? 'Sin hora este día' : 'Igual que el resto de días')}
                          </span>
                          {puedeEditarHoras && (estadoHora.propia || estadoHora.sinHora) && (
                            <button
                              type="button"
                              onClick={() => cambiar(pos, {
                                hora_inicio_servicio: cab.hora_inicio_servicio || '',
                                hora_fin_servicio: cab.hora_fin_servicio || '',
                                hora_servicio_manual: false,
                              })}
                              className="min-h-[40px] md:min-h-0 font-semibold text-[#11704F] hover:underline"
                              title={`Poner la hora de la cabecera${cab.hora_inicio_servicio ? ` (${cab.hora_inicio_servicio}${cab.hora_fin_servicio ? ` a ${cab.hora_fin_servicio}` : ''})` : ''}`}
                              data-testid={`menus-jornada-hora-resto-${pos}`}
                            >
                              {estadoHora.sinHora ? 'Poner la del resto' : 'Volver a la del resto'}
                            </button>
                          )}
                        </span>
                      )}
                      <label className="flex items-center gap-1.5 min-h-[40px] md:min-h-0 text-[13px] md:text-xs text-[#5B6B64]">
                        <input
                          type="checkbox"
                          className="w-5 h-5 md:w-auto md:h-auto"
                          checked={bebidaIncluida}
                          onChange={(e) => cambiar(pos, {
                            bebida_incluida: e.target.checked,
                            bebidas_por_persona: e.target.checked ? (Number(bebidasPorPersona) || 1) : 0,
                          })}
                          data-testid={`menus-jornada-bebida-${pos}`}
                        />
                        Bebida incluida
                      </label>
                      {bebidaIncluida && (
                        <label className="flex items-center gap-1.5 text-[13px] md:text-xs text-[#5B6B64]">
                          por persona
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={bebidasPorPersona || 1}
                            onChange={(e) => cambiar(pos, { bebidas_por_persona: parseInt(e.target.value, 10) || 1 })}
                            className="w-16 md:w-14 h-11 md:h-8 text-center text-base md:text-xs border border-[#CBD3CF] rounded-lg"
                          />
                        </label>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <div className="rounded-lg bg-[#F7F9F8] px-3 py-2 text-sm text-[#33443C] flex flex-wrap justify-between gap-2">
          <span>Unidades de menú en la jornada: <strong>{totalPersonas}</strong></span>
          <span>Importe estimado sin IVA: <strong>{formatEuros(importe)}</strong></span>
        </div>
      </section>
    </ModalJornada>
  );
};

export default EditarMenusJornadaModal;
